import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { StudentApp } from './studentApps';

/**
 * Going to another app, as the Öğrenci Paneli does it (its `useAppRedirect.ts`, and the comp's
 * `goApp`): a full-screen circular wipe from the icon that was pressed, the app's name,
 * "Yönlendiriliyorsun", and then the browser goes to the app's sign-in door in the SAME tab. The
 * timings are the panel's, which are the comp's.
 */

/**
 * From the press on an app icon to the browser leaving: long enough for the wipe to finish and for
 * the name, the line under it and "Vazgeç" to be there and read (the comp's own timeline is all in
 * by about 1.7 s). The redirect screen then stays up for as long as the app takes to answer, which
 * is the point of it: it is the loading screen of the single sign-on hop.
 */
export const NAVIGATE_AFTER_MS = 1800;

/**
 * If the page is still here this long after it started to leave, the app did not answer (or the
 * student stopped the load): the screen goes away and says so, instead of staying over a page that
 * is not going anywhere.
 */
export const GIVE_UP_AFTER_MS = 15_000;

/** The comp's exit (`spWipeOut .45s`). */
export const LEAVE_MS = 450;

export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/** Where the comp's wipe grows from: its fixed `88% 6%`, by the header icons. */
const FALLBACK_ORIGIN = { x: 88, y: 6 } as const;

/** The comp shows DigiKoç in the corner only where there is room: at least 1100 x 700. */
const PEEK_MIN = { width: 1100, height: 700 } as const;

export interface ActiveRedirect {
  app: StudentApp;
  /** `in` while it is covering the page, `out` while it fades away after "Vazgeç". */
  phase: 'in' | 'out';
  /** Where the wipe starts, in % of the window: the centre of the icon that was pressed. */
  origin: { x: number; y: number };
  /** Whether DigiKoç waves from the corner. */
  peek: boolean;
}

/** The whole-page navigation to a door. A function of its own so that a test can stand in for it. */
export function goToDoor(href: string): void {
  window.location.assign(href);
}

const percent = (value: number) => Math.round(Math.min(100, Math.max(0, value)) * 10) / 10;

function originOf(element: Element): { x: number; y: number } {
  const rect = element.getBoundingClientRect();
  if ((rect.width === 0 && rect.height === 0) || window.innerWidth === 0 || window.innerHeight === 0) {
    return FALLBACK_ORIGIN;
  }
  return {
    x: percent(((rect.left + rect.width / 2) / window.innerWidth) * 100),
    y: percent(((rect.top + rect.height / 2) / window.innerHeight) * 100),
  };
}

/**
 * `open` is an `onClick` for the link to the door. What it does for the cases the comp's demo never
 * met:
 *
 *  - Reduced motion: no wipe. `open` does not take the click over, so the link's own navigation
 *    happens at once. (So it does with a modified click, which is a request for another tab, and
 *    with a script that never loaded: the link is a real link first.)
 *  - "Vazgeç" and Escape: cancel, even after the browser has started to leave (`window.stop()`
 *    ends a navigation that has not committed), and give focus back to the icon.
 *  - The page coming back from the back/forward cache with the screen still up: `pageshow` and
 *    `pagehide` with `persisted` put it away, so Back never lands on a stuck overlay.
 *  - The app not answering: after GIVE_UP_AFTER_MS the screen leaves and `onGiveUp` says why.
 *
 * Nothing here talks to the server, and nothing here is a timer that asks for anything: the timers
 * only move this screen.
 */
export function useAppRedirect(go: (href: string) => void = goToDoor, onGiveUp?: (app: StudentApp) => void) {
  const [active, setActive] = useState<ActiveRedirect | null>(null);
  const current = useRef<ActiveRedirect | null>(null);
  const opener = useRef<HTMLElement | null>(null);
  const timers = useRef<Record<'leave' | 'go' | 'giveUp', ReturnType<typeof setTimeout> | null>>({
    leave: null,
    go: null,
    giveUp: null,
  });
  // The latest callbacks, read when a timer fires: the timers outlive the render that made them.
  const callbacks = useRef({ go, onGiveUp });
  useEffect(() => {
    callbacks.current = { go, onGiveUp };
  });

  const clearTimers = useCallback(() => {
    for (const key of ['leave', 'go', 'giveUp'] as const) {
      const timer = timers.current[key];
      if (timer !== null) clearTimeout(timer);
      timers.current[key] = null;
    }
  }, []);

  const set = useCallback((next: ActiveRedirect | null) => {
    current.current = next;
    setActive(next);
  }, []);

  /** Away it goes: the wipe's exit, then gone, with focus back where the student was. */
  const leave = useCallback(() => {
    clearTimers();
    if (current.current === null) return;
    set({ ...current.current, phase: 'out' });
    timers.current.leave = setTimeout(() => {
      timers.current.leave = null;
      // The screen is taken out of the page, and the page behind it made live again, BEFORE focus goes
      // back: the page is `inert` while the screen is up, and an element in an inert subtree cannot
      // take focus (the browser drops the call without a word). A plain `set(null)` only schedules
      // that, so it is flushed first.
      flushSync(() => set(null));
      const back = opener.current;
      opener.current = null;
      if (back?.isConnected) back.focus();
    }, LEAVE_MS);
  }, [clearTimers, set]);

  const cancel = useCallback(() => {
    // A navigation that has begun but not committed is ended; one that has is already gone.
    try {
      window.stop();
    } catch {
      // Nothing to stop, or no way to: the screen goes away all the same.
    }
    leave();
  }, [leave]);

  const open = useCallback(
    (app: StudentApp, event: MouseEvent<HTMLElement>) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (window.matchMedia?.(REDUCED_MOTION_QUERY).matches) return;
      event.preventDefault();
      if (current.current !== null) return;

      opener.current = event.currentTarget;
      set({
        app,
        phase: 'in',
        origin: originOf(event.currentTarget),
        peek: window.innerWidth >= PEEK_MIN.width && window.innerHeight >= PEEK_MIN.height,
      });
      timers.current.go = setTimeout(() => {
        timers.current.go = null;
        callbacks.current.go(app.href);
        timers.current.giveUp = setTimeout(() => {
          timers.current.giveUp = null;
          leave();
          callbacks.current.onGiveUp?.(app);
        }, GIVE_UP_AFTER_MS);
      }, NAVIGATE_AFTER_MS);
    },
    [leave, set],
  );

  // The back/forward cache keeps a page as it was, timers and screen included. Put the screen
  // away as the page goes in AND as it comes out, so that there is no frame of it either way.
  useEffect(() => {
    const reset = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      clearTimers();
      opener.current = null;
      set(null);
    };
    window.addEventListener('pagehide', reset);
    window.addEventListener('pageshow', reset);
    return () => {
      window.removeEventListener('pagehide', reset);
      window.removeEventListener('pageshow', reset);
      clearTimers();
    };
  }, [clearTimers, set]);

  return { active, open, cancel };
}
